import React, { useState } from 'react';
import {
  Form,
  Input,
  Button,
  Table,
  Switch,
  Card,
  Row,
  Col,
  Typography,
  message,
  Alert,
  Tag,
  Space,
  Modal,
  Divider,
  Statistic,
  Progress,
  Tooltip,
  Radio
} from 'antd';
import {
  SearchOutlined,
  SettingOutlined,
  InfoCircleOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import API from '../services/api';

const { Title, Text } = Typography;
const { TextArea } = Input;

const ApplicationRules = () => {
  const [form] = Form.useForm();
  const [portForm] = Form.useForm();
  
  // 数据状态
  const [hostIp, setHostIp] = useState('');
  const [rules, setRules] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [switchLoading, setSwitchLoading] = useState({});
  const [passThroughStatus, setPassThroughStatus] = useState(0);
  const [passThroughLoading, setPassThroughLoading] = useState(false);
  
  // 模态框状态
  const [portModalVisible, setPortModalVisible] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);

  // 获取应用规则列表
  const fetchRules = async (ip) => {
    if (!ip) {
      message.warning('请输入Host IP地址');
      return;
    }

    setLoading(true);
    try {
      const response = await API.get('/api/admin/apply-rules', {
        params: { host_ip: ip }
      });
      
      if (response.data.code === 0) {
        setRules(response.data.data.list);
        setUsage(response.data.data);
        // 从API响应中获取流量直通状态
        setPassThroughStatus(response.data.data.pass_through || 0);
      } else {
        message.error(response.data.msg || '获取应用规则失败');
      }
    } catch (error) {
      console.error('获取应用规则失败:', error);
      if (error.response?.data?.msg) {
        message.error(error.response.data.msg);
      } else {
        message.error('获取应用规则失败');
      }
    } finally {
      setLoading(false);
    }
  };

  // 开启/禁用规则
  const handleStatusChange = async (ruleId, checked) => {
    setSwitchLoading(prev => ({ ...prev, [ruleId]: true }));
    
    try {
      const response = await API.put('/api/admin/apply-rule-status', {
        id: ruleId,
        host_ip: hostIp,
        status: checked ? 1 : 0
      });

      if (response.data.code === 0) {
        message.success(`规则${checked ? '开启' : '禁用'}成功`);
        // 刷新规则列表
        fetchRules(hostIp);
      } else {
        // 处理特殊错误情况
        if (response.data.msg?.includes('已达到单IP最大应用规则数量限制')) {
          message.error({
            content: response.data.msg,
            duration: 6,
            style: { marginTop: '20px' }
          });
        } else {
          message.error(response.data.msg || '操作失败');
        }
      }
    } catch (error) {
      console.error('修改规则状态失败:', error);
      // 检查是否是API返回的具体错误信息
      if (error.response?.data?.msg) {
        if (error.response.data.msg.includes('已达到单IP最大应用规则数量限制')) {
          message.error({
            content: error.response.data.msg,
            duration: 6,
            style: { marginTop: '20px' }
          });
        } else {
          message.error(error.response.data.msg);
        }
      } else {
        message.error('修改规则状态失败');
      }
    } finally {
      setSwitchLoading(prev => ({ ...prev, [ruleId]: false }));
    }
  };

  // 控制IP流量直通
  const handlePassThroughChange = async (checked) => {
    if (!hostIp) {
      message.warning('请先查询IP的规则列表');
      return;
    }

    if (!usage || !usage.apply_rule_id) {
      message.warning('未获取到apply_rule_id，无法设置流量直通');
      return;
    }

    setPassThroughLoading(true);
    
    try {
      // 使用查询返回数据中的apply_rule_id
      const applyRuleId = usage.apply_rule_id;
      
      const response = await API.put('/api/admin/apply-rule-pass-through', {
        id: applyRuleId, // 使用data中的apply_rule_id
        pass_through: checked ? 1 : 0
      });

      if (response.data.code === 0) {
        message.success(`IP ${hostIp} 流量直通${checked ? '开启' : '关闭'}成功`);
        setPassThroughStatus(checked ? 1 : 0);
        // 刷新规则列表
        fetchRules(hostIp);
      } else {
        message.error(response.data.msg || '操作失败');
      }
    } catch (error) {
      console.error('修改流量直通设置失败:', error);
      if (error.response?.data?.msg) {
        message.error(error.response.data.msg);
      } else {
        message.error('修改流量直通设置失败');
      }
    } finally {
      setPassThroughLoading(false);
    }
  };

  // 配置端口并开启规则
  const handlePortConfig = async (values) => {
    try {
      let ports = []; // 默认为空数组，表示使用默认端口
      
      // 如果用户明确选择了端口配置选项
      if (values.portConfigType === 'custom') {
        if (values.customPorts && values.customPorts.trim()) {
          // 解析端口配置，格式：111,222-333,444
          const portRanges = values.customPorts.split(',').map(range => {
            const trimmed = range.trim();
            if (trimmed.includes('-')) {
              const [start, end] = trimmed.split('-').map(p => parseInt(p.trim()));
              return [start, end];
            } else {
              const port = parseInt(trimmed);
              return [port, port];
            }
          });
          ports = portRanges;
        } else {
          // 用户选择了自定义但没输入内容，使用默认端口
          ports = [];
        }
      } else {
        // 默认模式使用空数组
        ports = [];
      }

      const response = await API.put('/api/admin/apply-rule-with-ports', {
        id: selectedRule.id,
        host_ip: hostIp,
        ports: ports
      });

      if (response.data.code === 0) {
        message.success(response.data.msg || '规则配置成功');
        setPortModalVisible(false);
        portForm.resetFields();
        setSelectedRule(null);
        // 刷新规则列表
        fetchRules(hostIp);
      } else {
        // 处理特殊错误情况
        if (response.data.msg?.includes('已达到单IP最大应用规则数量限制')) {
          message.error({
            content: response.data.msg,
            duration: 6,
            style: { marginTop: '20px' }
          });
        } else {
          message.error(response.data.msg || '配置失败');
        }
      }
    } catch (error) {
      console.error('配置规则失败:', error);
      // 检查是否是API返回的具体错误信息
      if (error.response?.data?.msg) {
        if (error.response.data.msg.includes('已达到单IP最大应用规则数量限制')) {
          message.error({
            content: error.response.data.msg,
            duration: 6,
            style: { marginTop: '20px' }
          });
        } else {
          message.error(error.response.data.msg);
        }
      } else {
        message.error('配置规则失败');
      }
    }
  };

  // 搜索表单提交
  const handleSearch = (values) => {
    setHostIp(values.hostIp);
    setPassThroughStatus(0); // 重置流量直通状态
    fetchRules(values.hostIp);
  };

  // 刷新数据
  const handleRefresh = () => {
    if (hostIp) {
      fetchRules(hostIp);
    }
  };

  // 显示端口配置模态框
  const showPortModal = (rule) => {
    setSelectedRule(rule);
    setPortModalVisible(true);
    // 重置表单到默认状态
    portForm.setFieldsValue({
      portConfigType: 'default',
      customPorts: undefined
    });
  };

  // 表格列定义
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: '规则名称',
      dataIndex: 'name',
      key: 'name',
      width: 150,
      render: (text) => (
        <Text 
          strong 
          style={{
            display: 'block',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {text}
        </Text>
      ),
    },
    {
      title: '规则说明',
      dataIndex: 'description',
      key: 'description',
      width: 100,
      render: (text) => {
        if (!text) return <Text type="secondary">-</Text>;
        
        const truncatedText = text.length > 30 ? `${text.substring(0, 30)}...` : text;
        
        return (
          <Tooltip title={text} placement="top">
            <Text 
              type="secondary" 
              style={{ 
                fontSize: '12px',
                cursor: 'pointer',
                display: 'block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {truncatedText}
            </Text>
          </Tooltip>
        );
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status, record) => (
        <Switch
          checked={status === 1}
          loading={switchLoading[record.id]}
          onChange={(checked) => handleStatusChange(record.id, checked)}
        />
      ),
    },

    {
      title: '端口配置',
      dataIndex: 'port_list',
      key: 'port_list',
      width: 100,
      render: (portList, record) => {
        if (!portList || portList.length === 0) {
          return <Tag color="default">默认端口</Tag>;
        }
        
        const portText = portList.join(', ');
        const isLong = portText.length > 10;
        const displayText = isLong ? `${portText.substring(0, 10)}...` : portText;
        
        return (
          <Tooltip title={portText} placement="top">
            <Tag 
              color="blue" 
              style={{ 
                cursor: 'pointer',
                maxWidth: '90px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'inline-block'
              }}
            >
              {displayText}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: '默认端口',
      dataIndex: 'default_port',
      key: 'default_port',
      width: 100,
      render: (port) => {
        if (!port) return <Tag color="green">-</Tag>;
        
        const portText = port.toString();
        const isLong = portText.length > 8;
        const displayText = isLong ? `${portText.substring(0, 8)}...` : portText;
        
        return (
          <Tooltip title={portText} placement="top">
            <Tag 
              color="green"
              style={{ 
                cursor: 'pointer',
                maxWidth: '70px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'inline-block'
              }}
            >
              {displayText}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      render: (_, record) => {
        const isDisabled = record.status === 1 || (usage && usage.app_enabled_count >= usage.app_max);
        const tooltipTitle = record.status === 1 
          ? "规则已开启，无需配置" 
          : (usage && usage.app_enabled_count >= usage.app_max)
            ? "已达到规则数量上限，请先禁用其他规则"
            : "配置端口并开启";
            
        return (
          <Space>
            <Button
              type="text"
              icon={<SettingOutlined />}
              onClick={() => showPortModal(record)}
              disabled={isDisabled}
              title={tooltipTitle}
            />
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: '24px', background: '#f5f5f5', minHeight: 'calc(100vh - 64px)' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <Title level={2} style={{ marginBottom: '24px' }}>
          应用规则管理
        </Title>

        {/* 搜索区域 */}
        <Card style={{ marginBottom: '24px' }}>
          <Form
            form={form}
            layout="inline"
            onFinish={handleSearch}
            style={{ marginBottom: '16px' }}
          >
            <Form.Item
              label="Host IP"
              name="hostIp"
              rules={[
                { required: true, message: '请输入Host IP地址' },
                { 
                  pattern: /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
                  message: '请输入有效的IP地址格式'
                }
              ]}
            >
              <Input
                placeholder="请输入IP地址"
                style={{ width: '300px' }}
                value={hostIp}
                onChange={(e) => setHostIp(e.target.value)}
              />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SearchOutlined />}
                  loading={loading}
                >
                  查询规则
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleRefresh}
                  disabled={!hostIp}
                >
                  刷新
                </Button>
              </Space>
            </Form.Item>
          </Form>

          {/* 使用统计 */}
          {usage && (
            <div>
              <Divider />
              <Row gutter={16}>
                <Col span={5}>
                  <Statistic
                    title="总规则数"
                    value={usage.count}
                    prefix={<InfoCircleOutlined />}
                  />
                </Col>
                <Col span={5}>
                  <Statistic
                    title="已开启"
                    value={usage.app_enabled_count}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Col>
                <Col span={5}>
                  <Statistic
                    title="最大限制"
                    value={usage.app_max}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Col>
                <Col span={4}>
                  <div>
                    <div style={{ marginBottom: '8px' }}>
                      <Tooltip title="针对整个IP的流量直通设置，开启后该IP的所有流量将直接通过">
                        流量直通 <InfoCircleOutlined style={{ color: '#1890ff' }} />
                      </Tooltip>
                    </div>
                    <Space direction="vertical" size="small">
                      <Switch
                        checked={passThroughStatus === 1}
                        loading={passThroughLoading}
                        onChange={handlePassThroughChange}
                        checkedChildren="开启"
                        unCheckedChildren="关闭"
                        disabled={!usage || !usage.apply_rule_id}
                      />
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {!usage || !usage.apply_rule_id 
                          ? '需要apply_rule_id' 
                          : passThroughStatus === 1 ? 'IP直通已开启' : 'IP直通已关闭'
                        }
                      </Text>
                    </Space>
                  </div>
                </Col>
                <Col span={5}>
                  <div>
                    <div style={{ marginBottom: '8px' }}>使用率</div>
                    <Progress
                      percent={Math.round((usage.app_enabled_count / usage.app_max) * 100)}
                      status={usage.app_enabled_count >= usage.app_max ? 'exception' : 'active'}
                      strokeColor={usage.app_enabled_count >= usage.app_max ? '#ff4d4f' : '#52c41a'}
                    />
                    <Text type="secondary">
                      {usage.usage_info.usage_rate}
                    </Text>
                  </div>
                </Col>
              </Row>
              
              {/* 限制提示 */}
              {usage.app_enabled_count >= usage.app_max && (
                <Alert
                  message="规则数量已达上限"
                  description={`当前IP已达到最大应用规则数量限制（${usage.app_max}个），无法开启更多规则。请先禁用其他规则后再开启新规则。`}
                  type="warning"
                  showIcon
                  style={{ marginTop: '16px' }}
                />
              )}
              {usage.app_enabled_count >= usage.app_max * 0.8 && usage.app_enabled_count < usage.app_max && (
                <Alert
                  message="接近规则数量上限"
                  description={`当前已开启 ${usage.app_enabled_count} 个规则，接近最大限制 ${usage.app_max} 个。建议及时清理不需要的规则。`}
                  type="info"
                  showIcon
                  style={{ marginTop: '16px' }}
                />
              )}
            </div>
          )}
        </Card>

        {/* 规则列表 */}
        {hostIp && (
          <Card>
            <div style={{ marginBottom: '16px' }}>
              <Title level={4} style={{ margin: 0, marginBottom: '16px' }}>
                规则列表 - {hostIp}
              </Title>
              {usage && usage.app_enabled_count >= usage.app_max && (
                <Alert
                  message="已达到最大规则开启数量限制"
                  description="当前无法开启新规则，请先禁用其他规则后再试"
                  type="warning"
                  showIcon
                  style={{ marginBottom: '16px' }}
                />
              )}
            </div>
            
            <Table
              columns={columns}
              dataSource={rules}
              rowKey="id"
              loading={loading}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
              }}
              scroll={{ x: 1000 }}
            />
          </Card>
        )}

        {/* 端口配置模态框 */}
        <Modal
          title={`配置规则端口 - ${selectedRule?.name}`}
          open={portModalVisible}
          onCancel={() => {
            setPortModalVisible(false);
            portForm.resetFields();
            setSelectedRule(null);
          }}
          onOk={() => portForm.submit()}
          okText="应用配置"
        >
          <Form
            form={portForm}
            layout="vertical"
            onFinish={handlePortConfig}
            initialValues={{
              portConfigType: 'default'
            }}
          >
            <Alert
              message="端口配置说明"
              description="选择端口配置方式。自定义格式：111,222-333,444（支持单个端口和端口范围）"
              type="info"
              showIcon
              style={{ marginBottom: '16px' }}
            />

            <Form.Item
              label="端口配置方式"
              name="portConfigType"
              rules={[{ required: true, message: '请选择端口配置方式' }]}
            >
              <Radio.Group>
                <Radio value="default">
                  <div>
                    <div>使用默认端口</div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                      应用规则的默认端口配置
                    </div>
                  </div>
                </Radio>
                <Radio value="custom">
                  <div>
                    <div>自定义端口配置</div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                      指定特定的端口或端口范围
                    </div>
                  </div>
                </Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.portConfigType !== currentValues.portConfigType
              }
            >
              {({ getFieldValue }) => {
                return getFieldValue('portConfigType') === 'custom' ? (
                  <Form.Item
                    label="自定义端口配置"
                    name="customPorts"
                  >
                    <TextArea
                      rows={3}
                      placeholder="例如：111,222-333,444"
                    />
                  </Form.Item>
                ) : null;
              }}
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default ApplicationRules; 